import { type ReactNode, useState } from 'react';
import { cn } from '../cn';
import { Icon } from '../icons';
import { Button } from '../primitives';
import s from './history.module.css';
import w from './wizard.module.css';

/** Full-height history editor with autosave state and word count (INT-05). */
export function HistoryEditor({
  value,
  onChange,
  saved,
  words,
  onHelp,
  helpBusy,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  saved: ReactNode;
  words: number;
  onHelp: () => void;
  helpBusy?: boolean;
  label: string;
}) {
  return (
    <div className={s.editor}>
      <textarea
        className={s.body}
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={10}
        spellCheck
      />
      <div className={s.bar}>
        <span className={w.saved}>
          <span className={w.g} />
          {saved}
        </span>
        <span>
          {words} {words === 1 ? 'word' : 'words'}
        </span>
        <button type="button" className={s.wand} onClick={onHelp} disabled={helpBusy} aria-busy={helpBusy}>
          <Icon.Wand />
          {helpBusy ? 'Reading your history…' : 'Help me with this'}
        </button>
      </div>
    </div>
  );
}

/** The assistant's proposal. Nothing is added unless the patient accepts (INT-07). */
export function AssistantSuggestion({
  observation,
  addition,
  anchor,
  onAccept,
  onEdit,
  onSkip,
}: {
  observation: string;
  addition: string | null;
  anchor: string | null;
  onAccept: () => void;
  onEdit: () => void;
  onSkip: () => void;
}) {
  return (
    <div className={s.assist}>
      <span className={s.lab}>
        <Icon.Wand />
        Writing assistant · a suggestion, not a medical statement
      </span>
      <div className={s.h}>{observation}</div>
      {addition && (
        <div className={s.sug}>
          {anchor ? `After "${anchor}": ` : 'At the end: '}
          <em>{addition}</em>
        </div>
      )}
      <div className={s.row}>
        {addition && <Button onClick={onAccept}>Add it, I'll fill in the blanks</Button>}
        {addition && (
          <Button variant="sec" onClick={onEdit}>
            Edit first
          </Button>
        )}
        <Button variant="ghost" onClick={onSkip}>
          Skip
        </Button>
        <span className={s.fine}>Nothing is added unless you accept it. The final words are yours.</span>
      </div>
    </div>
  );
}

/** Numbered questions for the panel (INT-06): each reads as text with Edit, as on the prototype. */
export function QuestionList({
  questions,
  onChange,
  max,
}: {
  questions: string[];
  onChange: (next: string[]) => void;
  max: number;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const commit = () => {
    if (editing === null) return;
    const text = draft.trim();
    const next =
      editing >= questions.length
        ? text
          ? [...questions, text]
          : questions
        : text
          ? questions.map((q, i) => (i === editing ? text : q))
          : questions.filter((_, i) => i !== editing);
    setEditing(null);
    setDraft('');
    if (next !== questions) onChange(next);
  };
  const edit = (i: number) => {
    commit();
    setEditing(i);
    setDraft(questions[i] ?? '');
  };
  return (
    <div className={s.qlist}>
      {questions.map((q, i) =>
        editing === i ? (
          <QuestionEditor
            key={q}
            n={i + 1}
            value={draft}
            onChange={setDraft}
            onDone={commit}
            onRemove={() => {
              setEditing(null);
              onChange(questions.filter((_, j) => j !== i));
            }}
          />
        ) : (
          <div key={q} className={s.q}>
            <span className={s.n}>{i + 1}</span>
            <span className={s.t}>{q}</span>
            <button
              type="button"
              className={s.x}
              onClick={() => edit(i)}
              aria-label={`Edit question ${i + 1}`}
            >
              Edit
            </button>
          </div>
        ),
      )}
      {editing !== null && editing >= questions.length ? (
        <QuestionEditor
          n={questions.length + 1}
          value={draft}
          onChange={setDraft}
          onDone={commit}
          onRemove={() => {
            setEditing(null);
            setDraft('');
          }}
        />
      ) : (
        questions.length < max && (
          <button type="button" className={cn(s.q, s.new)} onClick={() => edit(questions.length)}>
            <span className={s.n}>{questions.length + 1}</span>
            <span className={s.t}>Add another question</span>
          </button>
        )
      )}
    </div>
  );
}

function QuestionEditor({
  n,
  value,
  onChange,
  onDone,
  onRemove,
}: {
  n: number;
  value: string;
  onChange: (v: string) => void;
  onDone: () => void;
  onRemove: () => void;
}) {
  return (
    <div className={cn(s.q, s.editing)}>
      <span className={s.n}>{n}</span>
      <span className={s.t}>
        <textarea
          // biome-ignore lint/a11y/noAutofocus: the user just asked to edit this question
          autoFocus
          rows={1}
          aria-label={`Question ${n}`}
          value={value}
          maxLength={1000}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onDone();
            }
          }}
        />
      </span>
      <span className={s.acts2}>
        <button type="button" className={s.x} onClick={onDone}>
          Done
        </button>
        <button type="button" className={s.x} onClick={onRemove}>
          Remove
        </button>
      </span>
    </div>
  );
}

export { s as historyStyles };
